import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useMaquinasEquipItensCadastroPagination } from "../hooks/MaquinasEquipItens_cadastro/pagination";
import { MaquinasEquipItensForm } from "../schemas/MaquinasEquipItens_cadastro/MaquinasEquipItens_cadastro";
import { MaquinaEquipItem } from "../../domain/models/maquina_equip_item";

/**
 * LoadMaquinaEquipItensFuncState
 */

type LoadMaquinaEquipItensFuncState = {
  idMaquinaEquip?: number;
  page?: number | null;
  rowsPerPage?: number | null;
};

/**
 * useMaquinasEquipItensCadastroViewModel
 */

export const useMaquinasEquipItensCadastroViewModel = () => {
  const {
    controlePatrimonio: {
      getAllMaquinasEquipItensUseCase,
      createMaquinasEquipItensUseCase,
      updateMaquinasEquipItensUseCase,
      deleteMaquinasEquipItensUseCase,
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idMaquinaEquip, setIdMaquinaEquip] = useState<number>();

  const [maquinaEquipItems, setMaquinaEquipItems] = useState<MaquinaEquipItem[]>();
  const [selectedMaquinaEquipItem, setSelectedMaquinaEquipItem] = useState<MaquinaEquipItem>();

  const [maquinaEquipItemModal, setMaquinaEquipItemModal] = useState(false);
  const [maquinaEquipItemDeleteModal, setMaquinaEquipItemDeleteModal] = useState(false);

  const pagination = useMaquinasEquipItensCadastroPagination();

  /**
   * Funcoes de carregamento.
   */

  const loadMaquinaEquipItens = useCallback(async (fs?: LoadMaquinaEquipItensFuncState) => {
    try {
      const filterId = fs?.idMaquinaEquip ?? idMaquinaEquip;
      const filterPage = fs?.page ?? pagination.page;
      const filterRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllMaquinasEquipItensUseCase.getAllMaquinasEquipItens(
        filterId,
        filterPage,
        filterRowsPerPage,
      );

      setMaquinaEquipItems(getAllMaquinasEquipItensUseCase.items);
      pagination.setTotalItems(getAllMaquinasEquipItensUseCase.totalItems);
    } catch (e: unknown) {
      setMaquinaEquipItems([]);
      throw new Error("Falha ao buscar itens da máquina/equipamento!");
    }
  }, [
    pagination,
    getAllMaquinasEquipItensUseCase,
    idMaquinaEquip
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id da máquina/equipamento!");
        return;
      }
      setIdMaquinaEquip(Number(id));

      await loadMaquinaEquipItens({ idMaquinaEquip: Number(id) });

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);
    }
  });

  /**
   * Eventos.
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/MaquinasEquip_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadMaquinaEquipItens();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao recarregar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadMaquinaEquipItens,
    pushErrorNotification,
  ]);
  const handleMaquinaEquipItemOpenState = useCallback((idItemMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idItemMaquinaEquip && maquinaEquipItems) {
        setSelectedMaquinaEquipItem(maquinaEquipItems.find(item => item.idItemMaquinaEquip === idItemMaquinaEquip));
      } else {
        setSelectedMaquinaEquipItem(undefined);
      }
    }
    setMaquinaEquipItemModal(openState);
  }, [
    maquinaEquipItems,
  ]);

  const handleMaquinaEquipItemSubmit = useCallback(async (form: MaquinasEquipItensForm) => {
    if (idMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedMaquinaEquipItem) {
          await updateMaquinasEquipItensUseCase.updateMaquinasEquipItens(
            selectedMaquinaEquipItem.idItemMaquinaEquip,
            idMaquinaEquip,
            form.ordem,
            form.descricao,
          );
        } else {
          await createMaquinasEquipItensUseCase.createMaquinasEquipItens(
            idMaquinaEquip,
            form.ordem,
            form.descricao,
          );
        }
        await loadMaquinaEquipItens();
        setMaquinaEquipItemModal(false);
        pushInfoNotification("Sucesso", `Registro salvo com sucesso!`);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar item!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idMaquinaEquip,
    selectedMaquinaEquipItem,
    createMaquinasEquipItensUseCase,
    updateMaquinasEquipItensUseCase,
    loadMaquinaEquipItens,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleMaquinaEquipItemDeleteOpenState = useCallback((idItemMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idItemMaquinaEquip && maquinaEquipItems) {
        setSelectedMaquinaEquipItem(maquinaEquipItems.find(item => item.idItemMaquinaEquip === idItemMaquinaEquip));
      } else {
        setSelectedMaquinaEquipItem(undefined);
      }
    }
    setMaquinaEquipItemDeleteModal(openState);
  }, [
    maquinaEquipItems,
  ]);

  const handleMaquinaEquipItemDeleteSubmit = useCallback(async () => {
    if (idMaquinaEquip && selectedMaquinaEquipItem) {
      try {
        setSaving(true);
        setPageSaving(true);
        await deleteMaquinasEquipItensUseCase.deleteMaquinasEquipItens(idMaquinaEquip, selectedMaquinaEquipItem.idItemMaquinaEquip);
        await loadMaquinaEquipItens();
        setMaquinaEquipItemDeleteModal(false);
        pushInfoNotification("Sucesso", "Registro excluído com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir item!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idMaquinaEquip,
    selectedMaquinaEquipItem,
    deleteMaquinasEquipItensUseCase,
    loadMaquinaEquipItens,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMaquinasEquipItensCadastroPagination.setState({ page: page + 1 });
      await loadMaquinaEquipItens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadMaquinaEquipItens,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMaquinasEquipItensCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadMaquinaEquipItens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadMaquinaEquipItens,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    maquinaEquipItems: maquinaEquipItems ?? [],
    selectedMaquinaEquipItem: selectedMaquinaEquipItem ?? null,
    maquinaEquipItemModal,
    maquinaEquipItemDeleteModal,
    pagination,
    handleBack,
    handleReload,
    handleMaquinaEquipItemOpenState,
    handleMaquinaEquipItemSubmit,
    handleMaquinaEquipItemDeleteOpenState,
    handleMaquinaEquipItemDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
